import React, { useState, useCallback, useEffect } from 'react';
import { BusinessPartnerContext } from 'pages/BusinessPartnerManagement/BusinessPartnerContext';
import { commonPaginationConfig, initialPageParams } from 'models/page';
import { QueryKey } from 'api/queryKey';
import { useQuery, useQueryCache } from 'react-query';
import { BusinessPartnersAPI } from 'api/BusinessPartnersAPI';
import { Header } from 'common/components/Header';
import Column from 'antd/lib/table/Column';
import { useEnsureContext } from 'common/hooks';
import { UserStatusTag } from 'common/components/UserStatusTag';
import { Dropdown, Menu } from 'antd';
import { AdministrationGuard } from 'common/components/AdministrationGuard';
import { BusinessPartnerAdminsManagementRoutes } from 'pages/BusinessPartnerManagement/BusinessPartnerAdminsManagement/businessPartnerAdminsManagementRoutes';
import { Link, useRouteMatch } from 'react-router-dom';
import { toSimpleDateTime } from 'common/dateFnsUtils';
import { toSimpleDate } from 'common/dateFnsUtils';
import { useFormatMessage } from 'common/hooks/useFormatMessage';
import { FormattedMessage } from 'react-intl';
import { NumberedTable } from 'common/components/NumberedTable';
import { Admin } from 'models/adminUser';
import { DeleteAdminBySuperAdminButton } from 'pages/BusinessPartnerManagement/BusinessPartnerAdminsManagement/DeleteAdminBySuperAdminButton';
import { DeleteAdminByLocalAdminButton } from 'pages/BusinessPartnerManagement/BusinessPartnerAdminsManagement/DeleteAdminByLocalAdminButton';
import { useRole } from 'common/hooks/useRole';
import { AdminsFilters } from 'pages/BusinessPartnerManagement/BusinessPartnerAdminsManagement/AdminsFilter';
import { UserStatus } from 'models/userStatus';
import { UsersActivateButton } from 'pages/BusinessPartnerManagement/UsersActivateButton';
import { makeSubpartnersIdsString } from 'pages/BusinessPartnerManagement/BusinessPartnerMedicalOperatorsManagement/BusinessPartnerMedicalOperatorsPage/utils/makeSubpartnersIDsString';
import { isNil } from 'ramda';

export const BusinessPartnerAdminsPage = () => {
  const { url } = useRouteMatch();
  const [pageMetaData, setPageMetaData] = useState(initialPageParams);
  const [filters, setFilters] = useState({});
  const [filteredPartnerId, setFilteredPartnerId] = useState(0);
  const { isSuperAdmin } = useRole();
  const queryCache = useQueryCache();
  const bpContext = useEnsureContext(BusinessPartnerContext);
  const { isMedicalOperator } = useRole();
  const shouldHidePartnerColumn =
    !isNil(bpContext.parentPartnerId) || isMedicalOperator;
  const IDlist: number[] = [];
  const [IDsToGetData, setIDsToGetData] = useState('');

  const invalidateAdmins = useCallback(
    () =>
      queryCache.invalidateQueries(QueryKey.BusinessPartnerAdminsList).then(),
    [queryCache]
  );

  const {
    data: subpartnersData,
    isLoading: isLoadingSubpartners,
    isSuccess: isSubpartnersSuccess,
  } = useQuery(
    [QueryKey.BusinessPartnersList],
    () => BusinessPartnersAPI.fetchSubpartners(bpContext.id),
    {
      onSuccess: (res) => {
        const IDsString = makeSubpartnersIdsString(res, IDlist);
        setIDsToGetData(IDsString);
        invalidateAdmins();
      },
      keepPreviousData: true,
    }
  );

  const { isFetching, data, refetch } = useQuery(
    [QueryKey.BusinessPartnerAdminsList, pageMetaData, filters, IDsToGetData],
    () =>
      BusinessPartnersAPI.fetchAdmins(
        filteredPartnerId !== 0 ? filteredPartnerId : bpContext.id,
        {
          subPartnersIds: filteredPartnerId !== 0 ? '' : IDsToGetData,
          ...pageMetaData,
          ...filters,
        }
      ),
    {
      enabled: isSubpartnersSuccess,
      keepPreviousData: true,
    }
  );

  const totalDataNumber = data?.totalElements;

  useEffect(() => {
    refetch();
  }, [totalDataNumber, IDsToGetData, subpartnersData, refetch]);

  const intlMessage = useFormatMessage();

  const paginationConfig = {
    ...commonPaginationConfig,
    pageSizeOptions: ['25', '50', '100'],
    current: pageMetaData?.page,
    pageSize: data?.size,
    total: data?.totalElements,
    onChange: (page: number, pageSize?: number) =>
      setPageMetaData((prev) => ({
        ...prev,
        page,
        ...(pageSize && { pageSize }),
      })),
  };

  return (
    <>
      <Header
        title={intlMessage({ id: 'admins' })}
        extra={[
          <AdministrationGuard
            key={`${url}/${BusinessPartnerAdminsManagementRoutes.Register}`}
          >
            <Link
              id="button-register-administrator"
              className="ant-btn ant-btn-primary"
              to={`${url}/${BusinessPartnerAdminsManagementRoutes.Register}`}
            >
              <FormattedMessage id="admins.register" />
            </Link>
          </AdministrationGuard>,
        ]}
      />

      <AdminsFilters
        shouldHidePartnerColumn={shouldHidePartnerColumn}
        onSubmit={(values) => {
          (values.partnerID === 0 || !values.partnerID) &&
            setFilteredPartnerId(0);
          values.partnerID && setFilteredPartnerId(values.partnerID);
          setPageMetaData((pageMetaData) => ({ ...pageMetaData, page: 1 }));
          setFilters(() => ({
            login: values.login,
            partnerID: values.partnerID,
          }));
        }}
      />

      <NumberedTable
        rowKey="userId"
        loading={isLoadingSubpartners || isFetching}
        dataSource={data?.content}
        pagination={paginationConfig}
        scroll={{ x: 'auto' }}
      >
        {!isLoadingSubpartners && !isFetching && (
          <>
            <Column title="Email/Login" dataIndex="login" />
            {!shouldHidePartnerColumn && (
              <Column
                title={intlMessage({ id: 'partner' })}
                dataIndex={['partnerDetails', 'name']}
              />
            )}
            <Column
              title={intlMessage({ id: 'create_date' })}
              dataIndex="createDate"
              render={(value) => toSimpleDateTime(value)}
            />
            <Column
              title={intlMessage({ id: 'familyUsers.lastLogin' })}
              dataIndex="lastLoginDate"
              render={(value) => (value === null ? '--' : toSimpleDate(value))}
              align="center"
            />

            <Column
              title={intlMessage({ id: 'status' })}
              dataIndex="accountStatus"
              render={(value) => <UserStatusTag status={value} />}
            />

            {!isMedicalOperator && (
              <Column
                title={intlMessage({ id: 'actions' })}
                key="id"
                align="right"
                render={(value, record: Admin) => {
                  const notArchived =
                    record.accountStatus !== UserStatus.Archived;
                  const notDeactivated =
                    record.accountStatus !== UserStatus.Deactivated;
                  const notDeleted =
                    record.accountStatus !== UserStatus.Deleted;
                  const isDeactivated =
                    record.accountStatus === UserStatus.Deactivated;

                  return (
                    <Dropdown.Button
                      overlay={
                        <Menu>
                          {notDeactivated && notArchived && notDeleted && (
                            <Menu.Item
                              key="deactivate"
                              id="menu-item-deactivate-admin"
                            >
                              <UsersActivateButton
                                actionType="deactivate"
                                userId={record.userId}
                                type="text"
                                userName={record.login}
                                onStatusChange={invalidateAdmins}
                              >
                                <FormattedMessage
                                  id="businessPartners.deactivate"
                                  tagName="span"
                                />
                              </UsersActivateButton>
                            </Menu.Item>
                          )}
                          {isDeactivated && (
                            <Menu.Item
                              key="activate"
                              id="menu-item-activate-admin"
                            >
                              <UsersActivateButton
                                actionType="activate"
                                userId={record.userId}
                                userName={record.login}
                                type="text"
                                onStatusChange={invalidateAdmins}
                              >
                                <FormattedMessage
                                  id="businessPartners.activate"
                                  tagName="span"
                                />
                              </UsersActivateButton>
                            </Menu.Item>
                          )}

                          {notDeleted && (
                            <Menu.Item key="remove" id="menu-item-remove-admin">
                              {isSuperAdmin ? (
                                <DeleteAdminBySuperAdminButton
                                  danger
                                  type="text"
                                  admin={record}
                                  onDeleted={invalidateAdmins}
                                />
                              ) : (
                                !isSuperAdmin && (
                                  <DeleteAdminByLocalAdminButton
                                    danger
                                    type="text"
                                    admin={record}
                                    onDeleted={invalidateAdmins}
                                  />
                                )
                              )}
                            </Menu.Item>
                          )}
                        </Menu>
                      }
                    />
                  );
                }}
              />
            )}
          </>
        )}
      </NumberedTable>
    </>
  );
};
